import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ShieldCheck, ArrowRight, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi, setPassengerSession, isPassengerAuthed } from "@/lib/passengerApi";
import { MajheBrand } from "@/components/majhe/MajheLayout";

export default function OtpLogin() {
  const [step, setStep] = useState("mobile");  // mobile | otp | name
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const otpRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (isPassengerAuthed()) navigate("/majhe", { replace: true }); }, [navigate]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!/^\d{10}$/.test(mobile.replace(/\D/g, "").slice(-10))) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      const { data } = await passengerApi.post("/auth/send-otp", { mobile });
      setDemoOtp(data.demo_otp);
      setStep("otp");
      setTimeout(() => otpRef.current?.focus(), 100);
      toast.success(`OTP sent · Demo code: ${data.demo_otp}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setSending(false); }
  };

  const verify = async (e, otpOverride) => {
    e?.preventDefault();
    const code = otpOverride ?? otp;
    if (!/^\d{4,6}$/.test(code)) {
      toast.error("Enter a 6-digit OTP");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await passengerApi.post("/auth/verify", { mobile, otp: code, name });
      setPassengerSession(data.token, data.user);
      toast.success(`Welcome${data.user.name ? ", " + data.user.name : ""}!`);
      navigate("/majhe", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect OTP");
    } finally { setVerifying(false); }
  };

  return (
    <div data-testid="majhe-login" className="majhe-app majhe-login-shell" style={{ minHeight: "100dvh", background: "#FFFFFF" }}>
      {/* Brand-gradient hero CARD (matches user's reference) on a clean white page */}
      <div className="px-5 pt-8" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 24px)" }}>
        <div className="majhe-on-dark text-white px-5 py-7"
          style={{
            background: "var(--majhe-card-grad)",
            borderRadius: 24,
            boxShadow: "0 24px 50px -20px rgba(91,46,229,0.45)",
            position: "relative",
            overflow: "hidden",
          }}>
          <div className="flex justify-center mb-5">
            <MajheBrand size="lg"/>
          </div>
          <h1 className="text-3xl font-extrabold leading-tight">Goa moves with you.</h1>
          <p className="text-white/85 text-sm mt-2 leading-relaxed">
            Smart tickets, transit cards & passes — at your fingertips. Powered by KTCL.
          </p>
          <div className="mt-4 flex items-center gap-2 text-[12px] text-white/85">
            <Lightning weight="fill" size={14} aria-hidden="true"/> QR Tickets · Live ETA · Card Recharge · Smart Pass
          </div>
        </div>
      </div>

      <div className="pt-6 pb-10 px-6 majhe-sheet-body" style={{ background: "#FFFFFF" }}>
        {step === "mobile" && (
          <form onSubmit={sendOtp} className="space-y-5">
            <div>
              <h2 className="text-2xl font-extrabold text-[#18142A]">Sign in</h2>
              <p className="text-sm text-[#7B7390] mt-1">Use your mobile number — your tickets, passes & cards will be saved.</p>
            </div>
            <div className="majhe-field">
              <label htmlFor="login-mobile" className="majhe-field__label">Mobile Number</label>
              <div className="majhe-field__icon" aria-hidden="true"><Phone size={18} weight="bold"/></div>
              <input id="login-mobile" data-testid="login-mobile-input" className="majhe-input" type="tel" autoFocus
                inputMode="numeric" placeholder="+91 99XXX XXXXX"
                aria-label="Mobile number, 10 digits, used to receive OTP and recall your bookings"
                aria-required="true"
                value={mobile} onChange={(e) => setMobile(e.target.value)}/>
            </div>
            <div className="majhe-field">
              <label htmlFor="login-name" className="majhe-field__label">Your Name (optional)</label>
              <div className="majhe-field__icon" aria-hidden="true"><UserIcon/></div>
              <input id="login-name" data-testid="login-name-input" className="majhe-input"
                placeholder="Saved for booking & cards"
                aria-label="Your name, optional"
                value={name} onChange={(e) => setName(e.target.value)}/>
            </div>
            <button data-testid="login-send-otp" type="submit" disabled={sending}
              aria-label={sending ? "Sending one time password, please wait" : "Send one time password to my mobile"}
              className="majhe-btn">
              {sending ? "Sending OTP…" : (<>Send OTP <ArrowRight size={16} weight="bold" aria-hidden="true"/></>)}
            </button>
            <div className="text-center text-[11px] text-[#7B7390]">
              By continuing you agree to KTCL passenger terms · Demo OTP: <b>123456</b>
            </div>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verify} className="space-y-5" aria-label="OTP verification form">
            <button type="button" onClick={() => setStep("mobile")}
              aria-label="Change mobile number"
              className="text-[#5B2EE5] text-sm font-semibold">← Change number</button>
            <div>
              <h2 className="text-2xl font-extrabold text-[#18142A]">Enter OTP</h2>
              <p className="text-sm text-[#7B7390] mt-1" role="status" aria-live="polite">
                Sent to <b>+91 {mobile.replace(/\D/g, "").slice(-10)}</b> · Demo OTP: <span className="text-[#5B2EE5] font-bold">{demoOtp}</span>
              </p>
            </div>
            <div className="majhe-field">
              <label htmlFor="login-otp" className="majhe-field__label">6-Digit OTP</label>
              <input id="login-otp" data-testid="login-otp-input" ref={otpRef} className="majhe-input majhe-otp-input" type="tel"
                inputMode="numeric" placeholder="______" maxLength={6}
                aria-label="Six digit OTP code"
                aria-required="true"
                autoComplete="one-time-code"
                style={{ letterSpacing: "0.5em", textAlign: "center", fontWeight: 700, fontSize: 20, paddingLeft: 18, paddingRight: 18 }}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}/>
            </div>
            <button data-testid="login-verify-otp" type="submit" disabled={verifying || otp.length < 4}
              aria-label={verifying ? "Verifying OTP" : "Verify OTP and sign in"}
              className="majhe-btn">
              {verifying ? "Verifying…" : "Verify & Continue"}
            </button>
            <button type="button" onClick={() => { setOtp(demoOtp); verify(null, demoOtp); }}
              data-testid="login-autofill-otp"
              aria-label="Auto fill demo OTP one two three four five six and sign in"
              className="majhe-btn majhe-btn--ghost">
              Auto-fill demo OTP
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
    </svg>
  );
}
